import React, { useState, useEffect } from "react";
import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerHeader,
    DrawerBody,
    Box,
    Spinner,
    useToast,
} from "@chakra-ui/react";
import { appRequest } from "routes";
import FileViewer from "components/common/FileViewerPdfAndImage/FileViewer";

type FilePreviewDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    files: any[];
    docId?: any
};

export default function FilePreviewDrawer({
    isOpen,
    onClose,
    files,
    docId
}: FilePreviewDrawerProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const [sample, setSample] = useState({
        url: "",
        file: null,
        type: null,
        buffer: null, // Still treating as 'any' or specific buffer type if needed elsewhere
    });

    useEffect(() => {
        if (isOpen && files && files.length > 0) {
            getFile(files[0]);
        } else {
            setSample({
                url: "",
                file: null,
                type: null,
                buffer: null,
            });
        }
    }, [isOpen, files]);

    const getFile = async (file: any) => {
        try {
            if (file?._id) {
                setLoading(true);
                const response: any = await appRequest("filesystem", "get", {
                    id: file._id,
                });

                if (response) {
                    let buffer =
                        typeof response === "string" ? JSON.parse(response) : response;

                    // Handle Node.js Buffer JSON representation if present
                    if (buffer && buffer.type === 'Buffer' && Array.isArray(buffer.data)) {
                        buffer = buffer.data;
                    }

                    buffer = new Uint8Array(buffer);

                    const blobType = file?.metadata?.contentType || file?.contentType || "application/pdf";

                    const fileURL = URL.createObjectURL(
                        new Blob([buffer], { type: blobType })
                    );

                    setSample({
                        url: fileURL,
                        file: file,
                        type: blobType,
                        buffer: buffer,
                    });
                }
            }
        } catch (error: any) {
            console.error("Error in getFile:", error);
            toast({
                title: "Error",
                description: error.message || "Failed to fetch file",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader color="teal.500" fontWeight="700">
                    File Preview
                </DrawerHeader>

                <DrawerBody p={4} height="full" overflow="hidden">
                    {loading ? (
                        <Box
                            h="100%"
                            w="100%"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Spinner size="xl" color="teal.500" />
                        </Box>
                    ) : (
                        <Box h="100%" w="100%">
                            {sample.url ? (
                                <FileViewer sample={sample} docId={docId} />
                            ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" h="100%">
                                    No file to display
                                </Box>
                            )}
                        </Box>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}
